/**
 * Desert pyramid and desert well, after vanilla's DesertPyramidPiece and the
 * desert_well feature.
 */

import { SEA_LEVEL } from '../../constants';
import { AIR, TERRACOTTA_IDS, TNT_ID, WATER_ID } from '../../blocks';
import { Biome } from '../biomes';
import type { TerrainGenerator } from '../terrain';
import { block, blueprint, type Builder, F, int, centredPiece, type Piece, type Rng, seedOf, type StructureType } from './kit';

const SANDSTONE = block('Sandstone');
const CUT = block('Cut Sandstone');
const CHISELED = block('Chiseled Sandstone');
const SMOOTH = block('Smooth Sandstone');
const ORANGE = TERRACOTTA_IDS.orange;
const BLUE = TERRACOTTA_IDS.blue;

/**
 * Desert pyramid, 21 x 21 (front at v = 0): a stepped sandstone pyramid with a
 * tower on each front corner, a cut-sandstone entrance between them, a 9 x 9
 * hall whose floor carries the orange-and-blue terracotta star, and under the
 * blue block a 3 x 3 shaft down to the treasure room — four chests, and nine
 * TNT under the floor waiting for whoever steps on the plate.
 */
function buildPyramid(b: Builder): void {
  const y = b.y;
  b.fill(-1, y + 1, -1, 21, y + 16, 21, AIR);
  b.foundations(0, 0, 20, 20, y, SANDSTONE, 8);
  // the stepped body
  for (let i = 0; i <= 9; i++) b.fill(i, y + 1 + i, i, 20 - i, y + 1 + i, 20 - i, SANDSTONE);
  b.fill(10, y + 11, 10, 10, y + 11, 10, SMOOTH);
  // the hall and the corridors that run round it
  b.fill(5, y + 1, 5, 15, y + 4, 15, AIR);
  b.walls(4, y + 1, 4, 16, y + 4, 16, SANDSTONE);
  b.fill(8, y + 1, 8, 12, y + 6, 12, AIR);
  b.fill(9, y + 7, 9, 11, y + 7, 11, AIR);
  // pillars in the hall corners
  for (const [u, v] of [[6, 6], [14, 6], [6, 14], [14, 14]]) b.fill(u, y + 1, v, u, y + 4, v, CUT);
  // the terracotta star: an orange cross and ring around a blue centre
  for (let v = 6; v <= 14; v++) {
    for (let u = 6; u <= 14; u++) {
      const du = Math.abs(u - 10), dv = Math.abs(v - 10);
      if (du === dv && du <= 2) b.set(u, y, v, ORANGE);
      else if ((du === 0 && dv <= 3) || (dv === 0 && du <= 3)) b.set(u, y, v, ORANGE);
    }
  }
  b.set(10, y, 10, BLUE);
  // front towers: hollow, banded with orange terracotta, crowned in cut sandstone
  for (const u0 of [0, 16]) {
    b.fill(u0, y + 1, 0, u0 + 4, y + 10, 4, SANDSTONE);
    b.fill(u0 + 1, y + 1, 1, u0 + 3, y + 9, 3, AIR);
    for (const by of [y + 5, y + 7]) b.walls(u0, by, 0, u0 + 4, by, 4, ORANGE);
    b.walls(u0, y + 10, 0, u0 + 4, y + 10, 4, CUT);
    b.set(u0 + 2, y + 6, 0, CHISELED); b.set(u0 + 2, y + 8, 0, CHISELED);
    b.set(u0 + 2, y + 1, 4, AIR); b.set(u0 + 2, y + 2, 4, AIR); // into the pyramid
  }
  // entrance: a cut-sandstone portal between the towers, with the orange diamond above
  b.fill(8, y + 1, 0, 12, y + 7, 3, SANDSTONE);
  b.fill(9, y + 1, 0, 11, y + 4, 4, AIR);
  b.walls(8, y + 5, 0, 12, y + 5, 0, CUT);
  b.set(10, y + 6, 0, ORANGE); b.set(9, y + 7, 0, ORANGE); b.set(11, y + 7, 0, ORANGE);
  b.set(10, y + 7, 0, CHISELED);
  // side doors on the hall's east and west walls
  for (const u of [4, 16]) { b.set(u, y + 1, 10, AIR); b.set(u, y + 2, 10, AIR); }
  for (const u of [0, 20]) { b.fill(u, y + 1, 9, u, y + 3, 11, SANDSTONE); b.fill(u, y + 1, 10, u, y + 2, 10, AIR); }
  b.fill(1, y + 1, 10, 3, y + 2, 10, AIR); b.fill(17, y + 1, 10, 19, y + 2, 10, AIR);

  // the treasure pit under the blue block
  const pit = y - 12;
  b.fill(7, pit - 2, 7, 13, y - 1, 13, SANDSTONE);
  b.fill(9, pit + 1, 9, 11, y - 1, 11, AIR);           // the shaft
  b.fill(8, pit + 1, 8, 12, pit + 3, 12, AIR);          // the treasure room
  b.fill(9, pit - 1, 9, 11, pit - 1, 11, TNT_ID);       // the trap
  b.set(10, pit, 10, block('Smooth Stone'));            // stands in for the pressure plate
  for (const [u, v, f] of [[8, 10, F.PU], [12, 10, F.NU], [10, 8, F.PV], [10, 12, F.NV]] as const) {
    b.fill(u, pit + 1, v, u, pit + 3, v, AIR);
    b.chest(u, pit + 1, v, f);
  }
  for (const [u, v] of [[8, 8], [12, 8], [8, 12], [12, 12]]) b.fill(u, pit + 1, v, u, pit + 3, v, CHISELED);
}

function desertPyramid(g: TerrainGenerator, x: number, z: number, rnd: Rng): Piece[] | null {
  const c = g.columnClimate(x, z);
  if (c.biome !== Biome.Desert || c.height < SEA_LEVEL + 1) return null;
  return [centredPiece(x, z, int(rnd, 4), 21, 21, c.height, true, seedOf(rnd), buildPyramid)];
}

// Desert well: a sandstone basin with a single water source, four posts and
// a slab-like roof (vanilla uses slabs; full blocks here).
const WELL = blueprint({ S: SANDSTONE, '~': WATER_ID, M: SMOOTH }, [
  ['SSSSS', 'SSSSS', 'SS~SS', 'SSSSS', 'SSSSS'],
  ['SS.SS', 'S...S', '..~..', 'S...S', 'SS.SS'],
  ['     ', ' S.S ', ' ... ', ' S.S ', '     '],
  ['     ', ' S.S ', ' ... ', ' S.S ', '     '],
  ['     ', ' MMM ', ' MSM ', ' MMM ', '     '],
]);

function desertWell(g: TerrainGenerator, x: number, z: number, rnd: Rng): Piece[] | null {
  const c = g.columnClimate(x, z);
  if (c.biome !== Biome.Desert || c.height < SEA_LEVEL + 1) return null;
  return [centredPiece(x, z, 0, 5, 5, c.height, true, seedOf(rnd), (b) => {
    b.fill(0, b.y + 1, 0, 4, b.y + 5, 4, AIR);
    b.fill(1, b.y - 2, 1, 3, b.y - 1, 3, SANDSTONE);
    b.stamp(WELL, 0, b.y - 1, 0);
  })];
}

export const DESERT_PYRAMID: StructureType = {
  name: 'Desert Pyramid', spacing: 32, separation: 8, salt: 14357617, reach: 1, generate: desertPyramid,
};
export const DESERT_WELL: StructureType = {
  name: 'Desert Well', spacing: 22, separation: 4, salt: 0x77e1, reach: 1, generate: desertWell,
};
